import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useNavigate } from 'react-router-dom';
import { routes } from '~/constants';
import { useOverlayContext, useStoreActions } from '~/contexts';
import { useDuplicateProposal } from '~/hooks/reactQuery/mutations/useDuplicateProposal';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useRedirectToProposal } from '~/hooks/useCreateAgreement';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { useUpdateRelationship } from '~/hooks/useUpdateRelationship';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { agreementChangeModel } from '~/models';
import ChangeAssigneeModal from './ChangeAssigneeModal';
import { RenameAgreementModal } from './RenameAgreementModal';
import { useRenameAgreement } from './useRenameAgreement';
const relationshipLoader = 'getRelationship';
function openRenameModal(showModal, currentTitle, renameAgreement, onSuccess) {
    return showModal({
        biName: 'rename_agreement_modal',
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: (param)=>{
            let { dismissModal } = param;
            return /*#__PURE__*/ _jsx(RenameAgreementModal, {
                dismissModal: dismissModal,
                currentTitle: currentTitle,
                renameAgreement: renameAgreement,
                onSuccess: onSuccess
            });
        }
    });
}
export function useAgreementRowActions(param) {
    let { onTerminateSuccess, onUpdateSuccess } = param;
    const overlayContext = useOverlayContext();
    const { api } = useStoreActions();
    const { t: errorTexts } = useLocaleKeys('errors');
    const navigate = useNavigate();
    const { downloadAgreementPdf } = useDownloadAgreementPdf();
    const { redirectToProposal } = useRedirectToProposal();
    const { duplicateProposal } = useDuplicateProposal({
        onSuccess: (newDraftId)=>redirectToProposal({
                proposalType: 'agreement',
                draftId: newDraftId
            }),
        withPageLoader: true
    });
    const { terminateAgreementById } = useTerminateAgreement();
    const { RENAME_AGREEMENT_ENDPOINT } = useFeatureFlags();
    const { renameAgreement } = useRenameAgreement();
    const updateRelationship = useUpdateRelationship();
    const onDownloadPDF = (agreement)=>{
        downloadAgreementPdf(agreement.id);
    };
    const onTerminateAgreement = (agreement)=>{
        terminateAgreementById({
            id: agreement.id,
            postAction: ()=>{
                onTerminateSuccess();
            }
        });
    };
    const onEditAgreement = (agreement)=>{
        navigate(routes.editAgreement(agreement.id));
    };
    const onDuplicateAgreement = (agreement)=>{
        duplicateProposal({
            relationshipId: agreement.id,
            draftId: agreement.draftId
        });
    };
    async function fetchRelationshipForRename(id) {
        overlayContext.startLoader(relationshipLoader);
        const relationship = await handleRequest(api.relationship.get(id));
        overlayContext.stopLoader(relationshipLoader);
        if (relationship.success) {
            return relationship.data;
        }
        overlayContext.showErrorToast(errorTexts.unexpected(), 'get-agr-error');
        return null;
    }
    const renameThroughAmendment = (relationship)=>(newTitle, onRenamed)=>updateRelationship(_object_spread_props(_object_spread({}, relationship), {
                changes: [
                    agreementChangeModel.makeTitleChange(newTitle)
                ],
                onSuccess: onRenamed
            }));
    const renameThroughTitleEndpoint = (id)=>(newTitle, onRenamed)=>renameAgreement({
                id,
                title: newTitle,
                onSuccess: onRenamed
            });
    async function onRenameAgreement(agreement, onSuccess) {
        const { showModal } = overlayContext;
        if (RENAME_AGREEMENT_ENDPOINT) {
            const rename = renameThroughTitleEndpoint(agreement.id);
            return openRenameModal(showModal, agreement.title, rename, onSuccess);
        }
        const relationship = await fetchRelationshipForRename(agreement.id);
        if (relationship) {
            await openRenameModal(showModal, relationship.title, renameThroughAmendment(relationship), onSuccess);
        }
    }
    function onChangeAssignee(agreement) {
        overlayContext.showModal({
            biName: 'change_assignee_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (p)=>/*#__PURE__*/ _jsx(ChangeAssigneeModal, _object_spread({
                    id: agreement.id,
                    onSuccess: async ()=>onUpdateSuccess()
                }, p))
        });
    }
    return {
        onEditAgreement,
        onRenameAgreement,
        onChangeAssignee,
        onDuplicateAgreement,
        onTerminateAgreement,
        onDownloadPDF
    };
}
